import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronRight, 
  Play, 
  Star, 
  Users, 
  BookOpen, 
  Award,
  ArrowRight,
  CheckCircle,
  Sparkles
} from 'lucide-react';

const PWAWelcomePage = ({ onLogin, onSignUp }) => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const features = [
    {
      icon: BookOpen,
      title: "Transformative Courses",
      description: "Deep spiritual formation through apostolic and prophetic training"
    },
    {
      icon: Users,
      title: "Community",
      description: "Connect with like-minded believers on the same journey"
    },
    {
      icon: Award,
      title: "Certifications",
      description: "Earn credentials as you complete each track"
    }
  ];

  const testimonials = [
    {
      name: "David O.",
      role: "Leadership Track Graduate",
      quote: "This academy changed my life. I now walk in my calling with confidence.",
      rating: 5
    },
    {
      name: "Grace M.",
      role: "Prophetic Track Student",
      quote: "Learning to hear God's voice has transformed my relationship with Him.",
      rating: 5
    },
    {
      name: "Samuel K.",
      role: "Minstrel Track Graduate",
      quote: "I discovered my true purpose in worship ministry through this course.",
      rating: 5
    }
  ];

  // Auto-rotate testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/40 via-black to-black"></div>
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-600/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-800/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto py-20">
          {/* OA Ministries Logo */}
          <div className="mb-8">
            <div className="relative w-32 h-32 mx-auto">
              {/* Outer glow ring */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/30 to-orange-500/30 rounded-full blur-xl animate-pulse"></div>
              
              {/* Logo container */}
              <div className="relative w-full h-full bg-gradient-to-br from-purple-600 via-purple-700 to-purple-900 rounded-full flex items-center justify-center shadow-2xl shadow-purple-500/40 border-2 border-purple-400/30">
                {/* Inner circle with text */}
                <div className="w-28 h-28 bg-gradient-to-br from-black/80 to-purple-900/80 rounded-full flex flex-col items-center justify-center border border-purple-500/20">
                  <span className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-white to-orange-300 tracking-wider">OA</span>
                  <span className="text-[8px] font-semibold text-purple-300 tracking-[0.2em] mt-0.5">MINISTRIES</span>
                </div>
              </div>
              
              {/* Decorative accent */}
              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-16 h-1 bg-gradient-to-r from-transparent via-orange-500 to-transparent rounded-full"></div>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
            <span className="bg-gradient-to-r from-white via-purple-200 to-purple-400 text-transparent bg-clip-text">
              Obed Ansu Academy
            </span>
          </h1>

          {/* Tagline */}
          <p className="text-xl sm:text-2xl text-purple-200 mb-4 font-light">
            A Journey That Marks You Forever
          </p>

          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-10">
            Transform your spiritual journey through apostolic mentorship, 
            prophetic training, and worship excellence.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <button
              onClick={() => navigate('/signup')}
              className="group bg-white text-purple-900 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-purple-100 transition-all transform hover:scale-105 flex items-center justify-center gap-2 shadow-xl"
            >
              Start Your Journey
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/login')}
              className="border-2 border-purple-500 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-purple-500/20 transition-all flex items-center justify-center gap-2"
            >
              I Have an Account
            </button>
          </div>

          {/* Stats */}
          <div className="flex justify-center gap-8 sm:gap-16 text-center">
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-purple-400">500+</div>
              <div className="text-gray-400 text-sm">Students</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-purple-400">4</div>
              <div className="text-gray-400 text-sm">Tracks</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-purple-400">20+</div>
              <div className="text-gray-400 text-sm">Courses</div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronRight className="w-8 h-8 text-purple-400 rotate-90" />
        </div>
      </div>

      {/* Welcome Video Section */}
      <div className="py-16 px-6 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-4xl mx-auto">
          {/* January Course Announcement */}
          <div className="mb-8 text-center">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-orange-500/20 to-purple-500/20 border border-orange-500/30 rounded-full px-6 py-3 mb-6">
              <Sparkles className="w-5 h-5 text-orange-400" />
              <span className="text-orange-300 font-semibold">New Cohort Starting January 2026!</span>
              <Sparkles className="w-5 h-5 text-orange-400" />
            </div>
          </div>
          
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              A Message from Pastor Obed Ansu
            </h2>
            <p className="text-gray-400 text-base sm:text-lg">
              Discover what awaits you on this transformative journey
            </p>
          </div>
          
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-purple-500/20">
            <div className="aspect-video">
              <iframe
                src="https://drive.google.com/file/d/1ziMutWiS3ogmJ6hI7nEc-DHVRlA5HPnG/preview"
                title="Welcome Message from Pastor Obed Ansu"
                className="w-full h-full"
                allow="autoplay; encrypted-media"
                allowFullScreen
              ></iframe>
            </div>
          </div>
          
          {/* Call to Action after video */}
          <div className="mt-8 text-center">
            <p className="text-purple-300 mb-4">Don't miss the January 2026 intake!</p>
            <button
              onClick={() => navigate('/signup')}
              className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-purple-600 hover:from-orange-600 hover:to-purple-700 rounded-xl text-white font-semibold text-lg transition-all transform hover:scale-105 shadow-lg"
            >
              <span>Reserve Your Spot</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 px-6 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
            What You'll Experience
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            More than courses — a complete transformation journey
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-all hover:scale-105"
              >
                <div className="w-14 h-14 bg-purple-600/20 rounded-2xl flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tracks Preview */}
      <div className="py-20 px-6 bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">
            Learning Tracks
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: "Spiritual Growth", icon: "🌱", color: "from-green-500 to-emerald-600" },
              { name: "Leadership", icon: "👑", color: "from-yellow-500 to-orange-600" },
              { name: "Prophetic", icon: "🔥", color: "from-red-500 to-pink-600" },
              { name: "Minstrel", icon: "🎵", color: "from-blue-500 to-purple-600" }
            ].map((track, index) => (
              <div 
                key={index}
                className={`bg-gradient-to-br ${track.color} rounded-2xl p-6 text-center transform hover:scale-105 transition-all cursor-pointer`}
                onClick={() => navigate('/courses')}
              >
                <div className="text-4xl mb-3">{track.icon}</div>
                <h3 className="font-semibold text-lg">{track.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="py-20 px-6 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-12">
            What Our Students Say
          </h2>

          <div className="relative">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className={`transition-all duration-500 ${
                  index === currentSlide ? 'opacity-100 translate-x-0' : 'opacity-0 absolute inset-0'
                }`}
              >
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 sm:p-12">
                  <div className="flex justify-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                  <p className="text-xl sm:text-2xl text-gray-200 mb-6 italic">
                    "{testimonial.quote}"
                  </p>
                  <div>
                    <p className="font-semibold text-lg">{testimonial.name}</p>
                    <p className="text-purple-400 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentSlide ? 'bg-purple-500 w-6' : 'bg-gray-600'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="py-20 px-6 bg-gradient-to-r from-purple-900 via-purple-800 to-purple-900">
        <div className="max-w-4xl mx-auto text-center">
          <Sparkles className="w-12 h-12 text-purple-300 mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to Begin?
          </h2>
          <p className="text-purple-200 text-lg mb-8 max-w-2xl mx-auto">
            Join hundreds of believers who are discovering their purpose and walking in their divine assignment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/signup')}
              className="bg-white text-purple-900 px-10 py-4 rounded-2xl font-bold text-lg hover:bg-purple-100 transition-all transform hover:scale-105 flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-5 h-5" />
              Create Free Account
            </button>
            <button
              onClick={() => navigate('/about')}
              className="border-2 border-white/50 text-white px-10 py-4 rounded-2xl font-bold text-lg hover:bg-white/10 transition-all"
            >
              Learn More
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="py-8 px-6 bg-black border-t border-gray-800 text-center">
        <p className="text-gray-500 text-sm">
          © 2024 Obed Ansu Academy. A journey that marks you forever.
        </p>
      </div>
    </div>
  );
};

export default PWAWelcomePage;
